<template>
  <div class="drawer-root">
    <div class="drawer-scrim" @click="$emit('close')"></div>
    <aside class="drawer" :class="{ done: form.status === 'done' }" role="dialog" aria-modal="true">
      <!-- Header -->
      <header class="drawer-head">
        <div class="dh-titles">
          <span class="dh-eyebrow">{{ t('drawer.details') }} · #{{ task.id }}</span>
          <h2 class="dh-title">{{ form.title || task.title }}</h2>
        </div>
        <button class="icon-btn" :title="t('drawer.close')" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </header>

      <!-- Tabs -->
      <nav class="drawer-tabs">
        <button class="dt-tab" :class="{ active: tab === 'details' }" @click="tab = 'details'">{{ t('drawer.tabDetails') }}</button>
        <button class="dt-tab" :class="{ active: tab === 'activity' }" @click="tab = 'activity'">{{ t('drawer.tabActivity') }}</button>
        <button class="dt-tab" :class="{ active: tab === 'time' }" @click="tab = 'time'">{{ t('drawer.tabTime') }}</button>
      </nav>

      <div class="drawer-body">
        <!-- TAB 1 · Details — header, description, urgency, status, project… -->
        <div v-show="tab === 'details'">
          <div class="form-group">
            <label>{{ t('taskForm.title') }} *</label>
            <input v-model="form.title" class="form-control" maxlength="200" />
          </div>

          <div class="form-group">
            <label>{{ t('taskForm.description') }}</label>
            <!-- Editor mounts only after the detail fetch settles: the list row
                 has no description, and letting the user type into an editor the
                 fetch is about to overwrite would eat their input. -->
            <RichTextEditor v-if="descFetched" v-model="form.description" :placeholder="t('taskForm.descriptionPlaceholder')" />
            <div v-else class="desc-loading"><span class="ss-dot"></span></div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ t('taskForm.urgency') }}</label>
              <select v-model="form.urgency" class="form-control">
                <option value="low">{{ t('urgency.low') }}</option>
                <option value="normal">{{ t('urgency.normal') }}</option>
                <option value="high">{{ t('urgency.high') }}</option>
                <option value="critical">{{ t('urgency.critical') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ t('taskForm.status') }}</label>
              <select v-model="form.status" class="form-control">
                <option value="open">{{ t('status.open') }}</option>
                <option value="in_progress">{{ t('status.in_progress') }}</option>
                <option value="done">{{ t('status.done') }}</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ t('taskForm.project') }}</label>
              <select v-model="form.project_id" class="form-control">
                <option :value="null">{{ t('common.none') }}</option>
                <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ t('taskForm.dueDate') }}</label>
              <input v-model="form.due_at" type="date" class="form-control" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ t('taskForm.visibility') }}</label>
              <select v-model="form.visibility" class="form-control" :disabled="lockedPrivate">
                <option value="shared">{{ t('projects.sharedBadge') }}</option>
                <option value="private">{{ t('projects.privateBadge') }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ t('taskForm.assignTo') }}</label>
              <div class="assignee-chips">
                <button
                  v-for="u in users"
                  :key="u.id"
                  type="button"
                  class="asg-chip"
                  :class="{ on: form.assignee_ids.includes(u.id) }"
                  @click="toggleAssignee(u.id)"
                >{{ u.name }}</button>
                <span v-if="!users.length" class="asg-empty">{{ t('taskForm.unassigned') }}</span>
              </div>
            </div>
          </div>

          <p v-if="error" class="form-error">{{ error }}</p>
          <div class="save-status" :class="savedState" aria-live="polite">
            <template v-if="savedState === 'saving'">
              <span class="ss-dot"></span>{{ t('drawer.saving') }}
            </template>
            <template v-else-if="savedState === 'saved'">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><polyline points="20 6 9 17 4 12"/></svg>{{ t('drawer.saved') }}
            </template>
          </div>
        </div>

        <!-- TAB 2 · Subtasks & comments -->
        <div v-show="tab === 'activity'">
          <section class="block flush">
            <TaskSubtasks :task-id="task.id" />
          </section>
          <section class="block">
            <TaskComments :task-id="task.id" />
          </section>
        </div>

        <!-- TAB 3 · Time log — timer + editable session log -->
        <div v-show="tab === 'time'">
          <section class="block flush">
            <div class="block-head">
              <h3 class="block-title">{{ t('sessions.title') }}</h3>
              <div class="block-tools">
                <TaskTimer
                  :task-id="task.id"
                  :timer-seconds="task.timer_seconds"
                  :timer-started-at="task.timer_started_at"
                  @update="onTimerUpdate"
                />
                <span class="sess-total">{{ t('sessions.total') }}: {{ totalLabel }}</span>
              </div>
            </div>

            <div class="sess-add">
              <input v-model="newSession.date" type="date" class="form-control sess-date" />
              <input v-model.number="newSession.minutes" type="number" min="0" step="1" class="form-control sess-min" :placeholder="t('sessions.minutes')" />
              <button class="btn btn-ghost btn-sm" :disabled="!canAdd" @click="addSession">{{ t('sessions.add') }}</button>
            </div>

            <p v-if="!sessions.length" class="muted-sm">{{ t('sessions.empty') }}</p>
            <ul v-else class="sess-list">
              <li v-for="s in sessions" :key="s.id" class="sess-row" :class="{ running: s.running, editing: editingId === s.id }">
                <!-- Edit panel: HH:MM spinner + date + cancel/update -->
                <div v-if="editingId === s.id" class="sess-edit">
                  <div class="se-owner">
                    <UserAvatar :user="ownerOf(s)" :size="28" />
                    <span class="se-owner-name">{{ s.user_name }}</span>
                  </div>
                  <div class="se-field se-time">
                    <label class="se-label">{{ t('sessions.duration') }}</label>
                    <div class="time-spin">
                      <div class="ts-unit">
                        <button type="button" class="ts-step" :title="t('sessions.hours')" @click="bump('hours', 1)">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
                        </button>
                        <span class="ts-num">{{ pad2(editBuf.hours) }}</span>
                        <button type="button" class="ts-step" @click="bump('hours', -1)">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                      </div>
                      <span class="ts-sep">:</span>
                      <div class="ts-unit">
                        <button type="button" class="ts-step" :title="t('sessions.minutes')" @click="bump('minutes', 1)">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="18 15 12 9 6 15"/></svg>
                        </button>
                        <span class="ts-num">{{ pad2(editBuf.minutes) }}</span>
                        <button type="button" class="ts-step" @click="bump('minutes', -1)">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="se-field se-date-field">
                    <label class="se-label">{{ t('taskForm.dueDate') }}</label>
                    <input type="date" class="form-control se-date" v-model="editBuf.date" />
                  </div>
                  <div class="se-actions">
                    <button type="button" class="btn btn-danger btn-sm" @click="cancelEdit">{{ t('common.cancel') }}</button>
                    <button type="button" class="btn btn-primary btn-sm" @click="saveEdit(s)">{{ t('sessions.update') }}</button>
                  </div>
                </div>

                <!-- Read row (running or closed) — click a closed row to edit -->
                <div v-else class="sess-view" :class="{ clickable: !s.running }" @click="!s.running && startEdit(s)">
                  <UserAvatar class="sess-av" :user="ownerOf(s)" :size="26" />
                  <span class="sess-cell sess-date-cell">{{ fmtDate(s.started_at) }}</span>
                  <span v-if="s.running" class="sess-cell sess-live">{{ t('sessions.running') }}</span>
                  <span v-else class="sess-cell sess-dur">{{ hm(s.minutes) }}</span>
                  <span class="sess-cell sess-src">{{ s.running || s.source !== 'manual' ? t('sessions.timer') : t('sessions.manual') }}</span>
                  <span class="sess-cell sess-actions" @click.stop>
                    <button v-if="!s.running" class="icon-btn sm danger" :title="t('subtasks.removeRow')" @click="removeSession(s)">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                    </button>
                  </span>
                </div>
              </li>
            </ul>
          </section>
        </div>
      </div>

      <!-- Footer actions — only for a done/archived task (reopen / restore). An
           active task has none: it saves automatically and closes via the ✕. -->
      <footer v-if="task.archived_at || task.status === 'done'" class="drawer-foot">
        <button v-if="task.archived_at" class="btn btn-ghost" @click="$emit('restore', task)">
          {{ t('tasks.restore') }}
        </button>
        <button v-else-if="task.status === 'done'" class="btn btn-ghost" @click="$emit('reopen', task)">
          {{ t('taskCard.reopen') }}
        </button>
      </footer>
    </aside>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import api from '@/api'
import TaskTimer from '@/components/TaskTimer.vue'
import TaskSubtasks from '@/components/TaskSubtasks.vue'
import TaskComments from '@/components/TaskComments.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import { useI18n } from '@/composables/useI18n'
import { useToast } from '@/composables/useToast'

function toLocalDate(str) {
  if (!str) return ''
  const d = new Date(str)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'TaskDetailDrawer',
  components: { TaskTimer, TaskSubtasks, TaskComments, RichTextEditor, UserAvatar },
  props: {
    task:     { type: Object, required: true },
    projects: { type: Array,  default: () => [] },
  },
  emits: ['close', 'saved', 'changed', 'complete', 'reopen', 'restore'],
  setup(props, { emit }) {
    const { t, locale } = useI18n()
    const toast = useToast()
    const error  = ref('')
    const users  = ref([])
    const tab    = ref('details')   // details | activity | time

    // List responses no longer carry `description` (payload weight) — it is
    // fetched from the single-task endpoint on open, see onMounted below.
    const form = reactive({
      title:       props.task.title       || '',
      description: props.task.description || '',
      urgency:     props.task.urgency     || 'normal',
      status:      props.task.status      || 'open',
      visibility:  props.task.visibility  || 'shared',
      project_id:  props.task.project_id  ?? null,
      assignee_ids: props.task.assignees?.map(a => a.id)
        ?? (props.task.assigned_to ? [props.task.assigned_to] : []),
      due_at:      toLocalDate(props.task.due_at),
    })

    function toggleAssignee(id) {
      const i = form.assignee_ids.indexOf(id)
      if (i === -1) form.assignee_ids.push(id)
      else form.assignee_ids.splice(i, 1)
      // the deep watcher below picks this up and auto-saves
    }

    const lockedPrivate = computed(() => {
      const p = props.projects.find(p => p.id === form.project_id)
      return p?.visibility === 'private'
    })
    watch(lockedPrivate, (locked) => { if (locked) form.visibility = 'private' }, { immediate: true })

    // ── time sessions ──
    const sessions   = ref([])
    const newSession = reactive({ date: toLocalDate(new Date().toISOString()), minutes: null })
    const canAdd     = computed(() => Number(newSession.minutes) > 0)
    const totalMinutes = computed(() => sessions.value.reduce((s, x) => s + (x.running ? 0 : x.minutes), 0))
    const totalLabel = computed(() => {
      const h = Math.floor(totalMinutes.value / 60)
      const m = totalMinutes.value % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    })

    async function loadSessions() {
      try { sessions.value = (await api.getSessions(props.task.id)).data || [] } catch {}
    }
    async function addSession() {
      if (!canAdd.value) return
      try {
        const res = await api.addSession(props.task.id, { date: newSession.date, minutes: Number(newSession.minutes) })
        sessions.value = res.data || []
        newSession.minutes = null
        emit('changed')
      } catch (e) { error.value = e.message }
    }
    // ── inline edit panel (HH:MM spinner) ──
    const editingId = ref(null)
    const editBuf   = reactive({ hours: 0, minutes: 0, date: '' })
    function pad2(n) { return String(n).padStart(2, '0') }
    function hm(mins) { const m = Math.max(0, mins | 0); return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}` }
    function ownerOf(s) {
      return { id: s.user_id, name: s.user_name, avatar_url: s.avatar_url, avatar_color: s.avatar_color }
    }
    function startEdit(s) {
      editingId.value = s.id
      editBuf.hours   = Math.floor((s.minutes || 0) / 60)
      editBuf.minutes = (s.minutes || 0) % 60
      editBuf.date    = dateOnly(s.started_at)
    }
    function cancelEdit() { editingId.value = null }
    // Chevron steps. Minutes roll over into hours; nothing drops below zero.
    function bump(field, delta) {
      if (field === 'hours') {
        editBuf.hours = Math.max(0, editBuf.hours + delta)
      } else {
        let total = editBuf.hours * 60 + editBuf.minutes + delta
        if (total < 0) total = 0
        editBuf.hours   = Math.floor(total / 60)
        editBuf.minutes = total % 60
      }
    }
    async function saveEdit(s) {
      const minutes = editBuf.hours * 60 + editBuf.minutes
      try {
        sessions.value = (await api.updateSession(props.task.id, s.id, { minutes, date: editBuf.date })).data || []
        editingId.value = null
        emit('changed')
      } catch { error.value = t('toast.saveFailed'); toast.error(t('toast.saveFailed')) }
    }
    async function removeSession(s) {
      if (!confirm(t('sessions.deleteConfirm'))) return
      try { sessions.value = (await api.deleteSession(props.task.id, s.id)).data || []; emit('changed') } catch {}
    }
    function onTimerUpdate() { loadSessions(); emit('changed') }

    // Auto-save: every change to the form persists on a short debounce — no Save
    // button. A subtle inline status ("saving…/saved") reflects state; errors toast.
    // `ready` gates the watcher so the initial mount (and the lockedPrivate sync
    // below) don't trigger a spurious save of unchanged data.
    let ready = false
    let saveTimer = null
    let dirty = false          // unsaved edits pending (debounced or in-flight)
    const savedState = ref('idle') // idle | saving | saved
    const descLoaded  = ref(false) // detail fetch delivered the description (safe to save it)
    const descFetched = ref(false) // detail fetch settled (success OR failure) — editor may mount

    async function persist() {
      if (!dirty) return
      dirty = false
      error.value = ''
      try {
        const payload = {
          title:       form.title,
          urgency:     form.urgency,
          status:      form.status,
          visibility:  form.visibility,
          project_id:  form.project_id  || null,
          assignee_ids: form.assignee_ids,
          due_at:      form.due_at      || null,
        }
        // Description is only written after the detail fetch delivered it —
        // otherwise a save before/without that fetch would wipe it to null.
        if (descLoaded.value) payload.description = form.description || null
        await api.updateTask(props.task.id, payload)
        savedState.value = 'saved'
        // Refresh the list (regroup/reorder) but DON'T emit 'saved' — that closes
        // the drawer. Auto-save must keep it open.
        emit('changed')
      } catch {
        dirty = true // let a later flush retry
        savedState.value = 'idle'
        error.value = t('toast.saveFailed')
        toast.error(t('toast.saveFailed'))
      }
    }

    watch(form, () => {
      if (!ready) return
      dirty = true
      savedState.value = 'saving'
      clearTimeout(saveTimer)
      saveTimer = setTimeout(persist, 600)
    }, { deep: true })

    // Flush any pending edit when the drawer closes/unmounts, so closing right
    // after a change (before the debounce fires) never loses it.
    onBeforeUnmount(() => {
      clearTimeout(saveTimer)
      persist()
    })

    function dateOnly(dt) { return dt ? dt.slice(0, 10) : '' }
    function fmtDate(dt) {
      if (!dt) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(dt).toLocaleDateString(loc, { day: '2-digit', month: 'short' })
    }

    onMounted(async () => {
      loadSessions()
      api.getUsers().then(r => { users.value = r.data || [] }).catch(() => {})
      // Lists ship without the (potentially huge) description — pull the full
      // task before arming the auto-save watcher, so the fetch-assign itself
      // never registers as a user edit.
      try {
        const full = (await api.getTask(props.task.id)).data
        if (full) {
          form.description = full.description || ''
          descLoaded.value = true
        }
      } catch {
        // Offline/failed: the rest of the form still works; description writes
        // stay disabled (persist() skips it while descLoaded is false).
      }
      descFetched.value = true
      ready = true
    })

    return {
      t, tab, form, users, savedState, descFetched, error, lockedPrivate, toggleAssignee,
      sessions, newSession, canAdd, totalLabel, addSession, removeSession, onTimerUpdate,
      dateOnly, fmtDate,
      editingId, editBuf, pad2, hm, ownerOf, startEdit, cancelEdit, bump, saveEdit,
    }
  },
}
</script>

<style scoped>
.drawer-root { position: fixed; inset: 0; z-index: 1100; }
.drawer-scrim { position: absolute; inset: 0; background: rgba(16,22,40,.42); }

/* Multi-assignee chip toggles */
.assignee-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.asg-chip {
  border: 1px solid transparent;
  background: var(--surface-2);
  color: var(--muted);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 13px; font-weight: 500;
  cursor: pointer; transition: background .12s, color .12s;
}
.asg-chip:hover { color: var(--text); }
.asg-chip.on { background: var(--accent-soft); color: var(--accent-ink); box-shadow: inset 0 0 0 1px var(--accent); }
.asg-empty { font-size: 13px; color: var(--faint); }

/* Floating, fully-rounded panel — soft edges, doesn't touch the screen edges. */
.drawer {
  position: absolute; inset-block: 14px; inset-inline-end: 14px;
  width: min(720px, calc(100% - 28px));
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(16,22,40,.22);
  display: flex; flex-direction: column;
  animation: drawer-in .2s ease;
}
@keyframes drawer-in { from { transform: translateY(8px); opacity: .4; } to { transform: none; opacity: 1; } }
.drawer.done { opacity: .96; }

.drawer-head {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 18px 20px; border-bottom: 1px solid var(--border);
}
.dh-titles { flex: 1; min-width: 0; }
.dh-eyebrow { font-family: var(--font-mono); font-size: 11px; color: var(--faint); letter-spacing: .03em; }
.dh-title { font-size: var(--fs-h4); font-weight: 700; margin-top: 3px; line-height: 1.3; }

/* Tab bar — sits between the header and the scrollable body. */
.drawer-tabs {
  display: flex; gap: 4px;
  padding: 8px 14px 0;
  border-bottom: 1px solid var(--border);
}
.dt-tab {
  border: none; background: none; cursor: pointer;
  padding: 9px 14px; margin-bottom: -1px;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  color: var(--muted);
  border-bottom: 2px solid transparent;
  transition: color .12s, border-color .12s;
  white-space: nowrap;
}
.dt-tab:hover { color: var(--text); }
.dt-tab.active { color: var(--accent-ink); border-bottom-color: var(--accent); }

.drawer-body { flex: 1; overflow-y: auto; padding: 18px 20px; }
/* First block in a tab has no preceding content — drop its divider/top spacing. */
.block.flush { border-top: none; padding-top: 0; margin-top: 0; }

/* TaskSubtasks / TaskComments carry their own "attached panel" chrome (border +
   side margins) meant for the inline list view. Inside the drawer tab that reads
   as a cramped box-in-a-box — flatten them so they sit flush and use full width. */
.drawer :deep(.subtasks-panel),
.drawer :deep(.comments-block) {
  margin: 0; padding: 0; border: none; background: none; border-radius: 0;
}
/* Give the subtask grid breathing room now that the drawer is wider. */
.drawer :deep(.sub-table) { width: 100%; }
.drawer :deep(.col-title) { width: auto; }

.form-group { margin-bottom: 14px; }
.form-group label { display: block; font-size: 13px; color: var(--muted); margin-bottom: 6px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

.form-error { color: var(--danger); font-size: 13px; margin: 4px 0 10px; }
/* Subtle auto-save status (replaces the old Save button). */
.save-status {
  display: flex; align-items: center; gap: 6px;
  min-height: 20px; margin-bottom: 8px;
  font-size: 12.5px; font-weight: 600; color: var(--muted);
  transition: color .2s;
}
.save-status.saved { color: var(--accent-ink); }
.save-status.saved svg { color: var(--accent); }
/* Placeholder shown while the task detail (description) is being fetched. */
.desc-loading {
  display: flex; align-items: center; justify-content: center;
  min-height: 88px; border-radius: var(--radius-sm); background: var(--surface-2);
}
.save-status .ss-dot,
.desc-loading .ss-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--muted); animation: ss-pulse 1s ease-in-out infinite;
}
@keyframes ss-pulse { 0%,100% { opacity: .3; } 50% { opacity: 1; } }

.block { border-top: 1px solid var(--border); padding-top: 16px; margin-top: 18px; }
.block-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
.block-title { font-size: 14px; font-weight: 700; }
.block-tools { display: flex; align-items: center; gap: 12px; }
.sess-total { font-family: var(--font-mono); font-size: 12px; color: var(--muted); }

.sess-add { display: flex; gap: 8px; margin-bottom: 12px; }
.sess-date { flex: 1; }
.sess-min { width: 92px; }

.muted-sm { font-size: 12px; color: var(--faint); }
.sess-list { list-style: none; display: flex; flex-direction: column; gap: 6px; }
.sess-row {
  border: 1px solid var(--border); border-radius: var(--radius-sm);
  background: var(--surface-2);
}
.sess-row.running { border-color: color-mix(in srgb, var(--accent) 40%, var(--border)); }
.sess-row.editing { background: var(--surface); border-color: color-mix(in srgb, var(--accent) 28%, var(--border)); }

.sess-view {
  display: grid; grid-template-columns: 26px 1fr auto auto 34px; align-items: center; gap: 10px;
  padding: 6px 10px; border-radius: var(--radius-sm);
}
.sess-view.clickable { cursor: pointer; }
.sess-view.clickable:hover { background: var(--hover); }
.sess-av { flex-shrink: 0; }
.sess-cell { font-size: 12.5px; min-width: 0; }
.sess-dur { font-family: var(--font-mono); font-variant-numeric: tabular-nums; text-align: end; color: var(--text); }
.sess-src { font-size: 10.5px; color: var(--faint); text-align: center; }
.sess-live { color: var(--accent); font-weight: 600; }
.sess-actions { display: flex; justify-content: flex-end; }

/* ── edit panel: owner · HH:MM spinner · date · actions ── */
.sess-edit {
  display: flex; align-items: center; flex-wrap: wrap; gap: 16px;
  padding: 14px 16px;
}
.se-owner { display: flex; align-items: center; gap: 8px; }
.se-owner-name { font-size: 13px; font-weight: 600; color: var(--text); }
.se-field { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.se-label { font-size: 11.5px; color: var(--muted); font-weight: 600; }
.se-date-field { align-items: stretch; }
.se-date { width: 150px; }
.se-time { margin-inline-start: auto; }

.time-spin { display: flex; align-items: center; gap: 10px; }
.ts-unit { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.ts-num {
  font-family: var(--font-mono); font-size: 22px; font-weight: 400;
  font-variant-numeric: tabular-nums; color: var(--text);
  min-width: 46px; text-align: center;
  padding: 2px 6px; border-radius: 10px; background: var(--surface-2);
}
.sess-row.editing .ts-num { background: var(--surface-2); }
.ts-sep { font-size: 20px; color: var(--muted); font-weight: 300; padding-bottom: 2px; }
.ts-step {
  border: none; background: none; cursor: pointer; color: var(--muted);
  display: inline-flex; padding: 2px; border-radius: 8px; line-height: 0;
  transition: color .12s, background .12s;
}
.ts-step:hover { color: var(--accent-ink); background: var(--accent-soft); }
.se-actions { display: flex; align-items: center; gap: 8px; margin-inline-start: auto; }

.icon-btn {
  border: none; background: none; cursor: pointer; color: var(--muted);
  display: inline-flex; padding: 5px; border-radius: 8px;
}
.icon-btn:hover { color: var(--text); background: var(--hover); }
.icon-btn.sm { padding: 3px; }
.icon-btn.danger:hover { color: #fff; background: var(--danger); }

.drawer-foot {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 20px; border-top: 1px solid var(--border); background: var(--surface);
}

@media (max-width: 560px) {
  .form-row { grid-template-columns: 1fr; }
  .drawer { width: 100%; }
}
</style>
